import React, { useState, useEffect, useRef } from 'react';
import { fetchAvailableSlots, fetchFreeBusy, isMSConfigured, BERATER, createBookingEvent, getGraphStatus } from '../../../utils/graphApi';
import { fetchBookings, fetchBookingLogs } from '../../../services/apiService';

function todayISO() {
  return new Date()?.toISOString()?.split('T')?.[0];
}

function addDays(dateISO, n) {
  const d = new Date(dateISO);
  d?.setDate(d?.getDate() + n);
  return d?.toISOString()?.split('T')?.[0];
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  // Parse YYYY-MM-DD directly — no Date object, no timezone issues
  const parts = dateStr.split('-');
  if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
  return dateStr;
}

function formatDateTime(isoStr) {
  if (!isoStr) return '-';
  try {
    return new Date(isoStr)?.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return isoStr; }
}

const inputStyle = {
  padding: '8px 12px',
  borderRadius: '8px',
  border: '1.5px solid #dde1e7',
  fontSize: '13px',
  color: '#1e2b38',
  background: '#f8fafc',
  outline: 'none',
};

const GraphApiPanel = () => {
  const configured = isMSConfigured();

  // --- Slots test state ---
  const [date, setDate] = useState(todayISO());
  const [type, setType] = useState('phone');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // --- /api/slots raw test ---
  const [apiTestLoading, setApiTestLoading] = useState(false);
  const [apiTestResult, setApiTestResult] = useState(null);
  const [apiTestError, setApiTestError] = useState(null);
  const [apiTestLatency, setApiTestLatency] = useState(null);
  const [apiTestTokenStatus, setApiTestTokenStatus] = useState(null);

  // --- Free/Busy side-by-side ---
  const [fbStartDate, setFbStartDate] = useState(todayISO());
  const [fbEndDate, setFbEndDate] = useState(addDays(todayISO(), 4));
  const [fbType, setFbType] = useState('phone');
  const [fbLoading, setFbLoading] = useState(false);
  const [fbResult, setFbResult] = useState(null);
  const [fbError, setFbError] = useState(null);

  // --- /api/create-event test ---
  const [ceDate, setCeDate] = useState(todayISO());
  const [ceTime, setCeTime] = useState('09:00');
  const [ceType, setCeType] = useState('phone');
  const [ceBerater, setCeBerater] = useState(0);
  const [ceVorname, setCeVorname] = useState('Test');
  const [ceNachname, setCeNachname] = useState('Buchung');
  const [ceEmail, setCeEmail] = useState('test@example.com');
  const [ceTelefon, setCeTelefon] = useState('+49 123 456789');
  const [ceLoading, setCeLoading] = useState(false);
  const [ceResult, setCeResult] = useState(null);
  const [ceError, setCeError] = useState(null);
  const [ceLatency, setCeLatency] = useState(null);

  // --- Gebuchte Termine ---
  const [bookings, setBookings] = useState([]);
  const [syncedRefs, setSyncedRefs] = useState(new Set());
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [bookingsLatency, setBookingsLatency] = useState(null);
  const [bookingsLastSync, setBookingsLastSync] = useState(null);

  // --- Real-time status badge ---
  const [graphStatus, setGraphStatus] = useState(null); // { connected, lastRefresh, latencyMs, error }
  const [statusLoading, setStatusLoading] = useState(false);
  const statusIntervalRef = useRef(null);

  const checkGraphStatus = async () => {
    setStatusLoading(true);
    try {
      const data = await getGraphStatus();
      setGraphStatus({
        connected: data?.connected === true,
        lastRefresh: data?.lastRefresh || null,
        latencyMs: data?.latencyMs ?? 0,
        error: data?.error || null,
        reason: data?.reason || null,
        debug: data?.debug || null,
        tenantId: data?.tenantId || null,
      });
    } catch (e) {
      setGraphStatus({ connected: false, latencyMs: 0, error: e?.message, reason: 'fetch_failed' });
    } finally {
      setStatusLoading(false);
    }
  };

  const loadBookings = async () => {
    setBookingsLoading(true);
    const t0 = performance.now();
    try {
      const [allBookings, allLogs] = await Promise.all([
        fetchBookings(),
        fetchBookingLogs('calendar_event_created'),
      ]);
      const latency = Math.round(performance.now() - t0);
      setBookings(allBookings || []);
      const synced = new Set((allLogs || [])?.map(l => l?.booking_ref)?.filter(Boolean));
      setSyncedRefs(synced);
      setBookingsLatency(latency);
      setBookingsLastSync(new Date()?.toISOString());
    } catch (e) {
      console.error('loadBookings error', e);
    } finally {
      setBookingsLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
    checkGraphStatus();
    // Poll status every 60s
    statusIntervalRef.current = setInterval(checkGraphStatus, 60000);
    return () => clearInterval(statusIntervalRef?.current);
  }, []);

  const runTest = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await fetchAvailableSlots(date, type);
      setResult(data);
    } catch (err) {
      setError(err?.message || 'Unbekannter Fehler');
    } finally {
      setLoading(false);
    }
  };

  const runApiSlotsTest = async () => {
    setApiTestLoading(true);
    setApiTestResult(null);
    setApiTestError(null);
    setApiTestLatency(null);
    setApiTestTokenStatus(null);
    const t0 = performance.now();
    try {
      const data = await fetchAvailableSlots(date, type);
      const latency = Math.round(performance.now() - t0);
      setApiTestLatency(latency);
      if (data?.mock) {
        setApiTestTokenStatus('mock');
        setApiTestError(data?.graphError || null);
      } else {
        setApiTestTokenStatus('live');
      }
      // Convert to flat slots array for display
      const slots = [...(data?.morning || []), ...(data?.afternoon || [])]?.map(s => ({
        time: s?.time,
        berater: { name: s?.berater, email: s?.beraterEmail },
      }));
      setApiTestResult({ slots, mock: data?.mock, reason: data?.reason, tokenStatus: data?.tokenStatus });
    } catch (err) {
      const latency = Math.round(performance.now() - t0);
      setApiTestLatency(latency);
      setApiTestTokenStatus('error');
      setApiTestError(err?.message || 'Netzwerkfehler');
    } finally {
      setApiTestLoading(false);
    }
  };

  const runFreeBusy = async () => {
    setFbLoading(true);
    setFbResult(null);
    setFbError(null);
    try {
      const beraterList = [
        { name: BERATER?.[0]?.name, email: BERATER?.[0]?.email },
        { name: BERATER?.[1]?.name, email: BERATER?.[1]?.email },
      ];

      // Use /api/free-busy which returns independent availability for BOTH beraters
      const data = await fetchFreeBusy(fbStartDate, fbEndDate);

      if (data?.error && !data?.days) {
        throw new Error(data.error);
      }

      const dayResults = (data?.days || []).map(day => ({
        date: day.date,
        isWeekend: day.isWeekend,
        slots: (day.slots || []).map(s => ({
          time: s.time,
          durationMin: s.durationMin,
          period: s.period,
          berater0: s.berater0 || { free: false },
          berater1: s.berater1 || { free: false },
          bothFree: s.bothFree || false,
          conflict: s.conflict || false,
        })),
        busyBlocks0: day.busyBlocks0 || [],
        busyBlocks1: day.busyBlocks1 || [],
      }));

      setFbResult({ days: dayResults, berater: data?.berater || beraterList, mock: data?.mock || false, reason: data?.reason || null });
    } catch (err) {
      setFbError(err?.message || 'Netzwerkfehler');
    } finally {
      setFbLoading(false);
    }
  };

  const runCreateEventTest = async () => {
    setCeLoading(true);
    setCeResult(null);
    setCeError(null);
    setCeLatency(null);
    const t0 = performance.now();
    const beraterObj = BERATER?.[ceBerater];
    try {
      const data = await createBookingEvent({
        beraterEmail: beraterObj?.email,
        beraterName: beraterObj?.name,
        date: ceDate,
        time: ceTime,
        consultationType: ceType,
        vorname: ceVorname,
        nachname: ceNachname,
        email: ceEmail,
        telefon: ceTelefon,
        bookingRef: 'TEST-' + Date.now(),
      });
      const latency = Math.round(performance.now() - t0);
      setCeLatency(latency);
      if (data?.success) {
        setCeResult(data);
      } else {
        setCeError(data?.error || 'Event-Erstellung fehlgeschlagen');
        setCeResult(data);
      }
    } catch (err) {
      const latency = Math.round(performance.now() - t0);
      setCeLatency(latency);
      setCeError(err?.message || 'Netzwerkfehler');
    } finally {
      setCeLoading(false);
    }
  };

  // --- Style helpers ---
  const badge = (ok) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '4px 12px',
    borderRadius: '100px',
    fontSize: '12px',
    fontWeight: 600,
    background: ok ? '#dcfce7' : '#fef3c7',
    color: ok ? '#16a34a' : '#d97706',
    border: '1px solid ' + (ok ? '#86efac' : '#fcd34d'),
  });

  const slotChip = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '5px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 600,
    background: '#dcfce7',
    color: '#16a34a',
    border: '1px solid #86efac',
    margin: '3px',
  };

  const allSlots = result ? [...(result?.morning || []), ...(result?.afternoon || [])] : [];

  const tokenStatusMap = {
    live: { bg: '#dcfce7', color: '#16a34a', border: '#86efac', label: 'Token OK - Live-Daten' },
    mock: { bg: '#fef3c7', color: '#d97706', border: '#fcd34d', label: 'Mock-Daten (Credentials fehlen oder Graph API nicht erreichbar)' },
    error: { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5', label: 'Fehler - Token konnte nicht abgerufen werden' },
  };

  const tokenBadgeStyle = apiTestTokenStatus ? tokenStatusMap?.[apiTestTokenStatus] || tokenStatusMap?.error : null;

  // Free/busy slot color
  const slotColor = (b0Free, b1Free) => {
    if (!b0Free && !b1Free) return { bg: '#fef2f2', border: '#fca5a5', label: 'Beide belegt' };
    if (b0Free && b1Free) return { bg: '#dcfce7', border: '#86efac', label: 'Beide frei' };
    return { bg: '#fff7ed', border: '#fed7aa', label: 'Teilweise frei' };
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #dde1e7', borderRadius: '16px', padding: '28px 32px', marginBottom: '28px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
        <div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#1e2b38', marginBottom: '4px' }}>Microsoft Graph API - Kalender-Test</div>
          <div style={{ fontSize: '12px', color: '#8a95a3' }}>Freie Slots und Kalendereintraege beider Berater live abfragen</div>
        </div>
        <span style={badge(configured)}>
          {configured ? 'Konfiguriert' : 'Nicht konfiguriert'}
        </span>
      </div>
      {/* === REAL-TIME STATUS BADGE === */}
      <div style={{ background: '#f8fafc', border: '1px solid #dde1e7', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e2b38' }}>Graph API Status</div>
          <button
            onClick={checkGraphStatus}
            disabled={statusLoading}
            style={{ padding: '5px 14px', borderRadius: '8px', border: '1.5px solid #dde1e7', background: '#fff', color: '#3E4954', fontSize: '12px', fontWeight: 600, cursor: statusLoading ? 'not-allowed' : 'pointer', opacity: statusLoading ? 0.6 : 1 }}
          >
            {statusLoading ? 'Prüfe...' : 'Aktualisieren'}
          </button>
        </div>
        {graphStatus ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '12px' }}>
            {/* Connection status */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '100px', fontSize: '12px', fontWeight: 700, background: graphStatus?.connected ? '#dcfce7' : '#fef2f2', color: graphStatus?.connected ? '#16a34a' : '#dc2626', border: '1px solid ' + (graphStatus?.connected ? '#86efac' : '#fca5a5') }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: graphStatus?.connected ? '#16a34a' : '#dc2626', display: 'inline-block', boxShadow: graphStatus?.connected ? '0 0 0 2px #bbf7d0' : '0 0 0 2px #fecaca' }} />
              {graphStatus?.connected ? 'Verbunden' : (graphStatus?.reason === 'credentials_missing' ? 'Credentials fehlen' : 'Nicht verbunden')}
            </div>
            {/* Last token refresh */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '100px', fontSize: '12px', fontWeight: 600, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>
              🔑 Token: {graphStatus?.lastRefresh ? formatDateTime(graphStatus?.lastRefresh) : 'Noch nicht abgerufen'}
            </div>
            {/* Sync latency */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '100px', fontSize: '12px', fontWeight: 600, background: graphStatus?.latencyMs < 500 ? '#f0fdf4' : graphStatus?.latencyMs < 2000 ? '#fefce8' : '#fef2f2', color: graphStatus?.latencyMs < 500 ? '#15803d' : graphStatus?.latencyMs < 2000 ? '#a16207' : '#dc2626', border: '1px solid ' + (graphStatus?.latencyMs < 500 ? '#bbf7d0' : graphStatus?.latencyMs < 2000 ? '#fef08a' : '#fca5a5') }}>
              ⚡ Latenz: {graphStatus?.latencyMs}ms
            </div>
            {graphStatus?.graphLatencyMs && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '100px', fontSize: '12px', fontWeight: 600, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>
                📊 Graph API: {graphStatus?.graphLatencyMs}ms
              </div>
            )}
            {graphStatus?.error && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '100px', fontSize: '12px', fontWeight: 600, background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                ⚠ {graphStatus?.error}
              </div>
            )}
          </div>
        ) : (
          <div style={{ marginTop: '10px', fontSize: '12px', color: '#94a3b8' }}>Status wird geladen...</div>
        )}
        {graphStatus?.error && (
          <div style={{ marginTop: '10px', padding: '10px 14px', borderRadius: '8px', background: '#fef2f2', border: '1px solid #fca5a5', fontSize: '12px', color: '#dc2626', fontFamily: 'monospace', wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
            <strong>🔴 Debug-Fehler:</strong> {graphStatus?.error}
            {graphStatus?.debug && (
              <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #fca5a5' }}>
                <strong>🔍 Azure AD Debug:</strong>
                <pre style={{ margin: '6px 0 0', fontSize: '11px', color: '#7f1d1d', background: '#fff5f5', padding: '8px', borderRadius: '6px', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                  {JSON.stringify(graphStatus?.debug, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Credentials overview — status from /api/health */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px', marginBottom: '20px' }}>
        {[
          { label: 'Tenant ID', ok: graphStatus?.connected || (graphStatus?.debug?.tenantId === 'set') || !!graphStatus?.tenantId },
          { label: 'Client ID', ok: graphStatus?.connected || (graphStatus?.debug?.clientId === 'set') },
          { label: 'Client Secret', ok: graphStatus?.connected || (graphStatus?.debug?.clientSecret === 'set') || (graphStatus?.debug?.secretSet === true) },
        ]?.map((item) => (
          <div key={item?.label} style={{ background: '#f1f5f9', borderRadius: '8px', padding: '10px 14px' }}>
            <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>{item?.label}</div>
            <div style={{ fontSize: '12px', color: item?.ok ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
              {item?.ok ? 'Gesetzt' : 'Fehlt'}
            </div>
          </div>
        ))}
      </div>
      {/* Berater cards */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
        {BERATER?.map((b) => (
          <div key={b?.id} style={{ background: '#f8fafc', border: '1px solid #dde1e7', borderRadius: '12px', padding: '16px 20px', flex: '1 1 240px' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#1e2b38', marginBottom: '4px' }}>{b?.name}</div>
            <div style={{ fontSize: '12px', color: '#8a95a3', marginBottom: '10px' }}>{b?.email}</div>
            <span style={badge(!!b?.email)}>{b?.email ? 'E-Mail konfiguriert' : 'Fehlt'}</span>
          </div>
        ))}
      </div>
      {/* === FREE/BUSY SIDE-BY-SIDE === */}
      <div style={{ background: '#f8fafc', border: '1px solid #dde1e7', borderRadius: '14px', padding: '20px 24px', marginBottom: '24px' }}>
        <div style={{ fontSize: '14px', fontWeight: 700, color: '#1e2b38', marginBottom: '4px' }}>Verfügbarkeit beider Berater (Nebeneinander)</div>
        <div style={{ fontSize: '12px', color: '#8a95a3', marginBottom: '16px' }}>Freie/belegte Slots beider Berater für einen Datumsbereich — Konflikte und gebuchte Slots hervorgehoben</div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Von</div>
            <input type="date" value={fbStartDate} onChange={(e) => setFbStartDate(e?.target?.value)} style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Bis</div>
            <input type="date" value={fbEndDate} onChange={(e) => setFbEndDate(e?.target?.value)} style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Art</div>
            <select value={fbType} onChange={(e) => setFbType(e?.target?.value)} style={inputStyle}>
              <option value="phone">Telefonberatung (30 Min)</option>
              <option value="onsite">Vor-Ort-Beratung (60 Min)</option>
            </select>
          </div>
          <button
            onClick={runFreeBusy}
            disabled={fbLoading}
            style={{ padding: '9px 20px', borderRadius: '10px', border: 'none', background: '#3E4954', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: fbLoading ? 'not-allowed' : 'pointer', opacity: fbLoading ? 0.7 : 1 }}
          >
            {fbLoading ? 'Lädt...' : 'Verfügbarkeit abfragen'}
          </button>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
          {[
            { bg: '#dcfce7', border: '#86efac', color: '#16a34a', label: 'Beide frei' },
            { bg: '#fff7ed', border: '#fed7aa', color: '#c2410c', label: 'Einer frei' },
            { bg: '#fef2f2', border: '#fca5a5', color: '#dc2626', label: 'Beide belegt (Konflikt)' },
            { bg: '#f1f5f9', border: '#dde1e7', color: '#64748b', label: 'Wochenende / Geschlossen' },
          ]?.map(l => (
            <div key={l?.label} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: l?.color, fontWeight: 600 }}>
              <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: l?.bg, border: '1px solid ' + l?.border, display: 'inline-block' }} />
              {l?.label}
            </div>
          ))}
        </div>

        {fbError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', fontSize: '12px', color: '#dc2626' }}>
            <strong>Fehler:</strong> {fbError}
          </div>
        )}

        {fbResult && (
          <div>
            {fbResult?.mock && (
              <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px', padding: '8px 14px', marginBottom: '12px', fontSize: '12px', color: '#d97706', fontWeight: 600 }}>
                ⚠ Mock-Daten — Graph API nicht erreichbar ({fbResult?.reason})
              </div>
            )}

            {/* Side-by-side grid */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '600px' }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #dde1e7', whiteSpace: 'nowrap' }}>Datum</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #dde1e7', whiteSpace: 'nowrap' }}>Uhrzeit</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: '#3b82f6', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #dde1e7', whiteSpace: 'nowrap' }}>
                      {fbResult?.berater?.[0]?.name || 'Berater 1'}
                    </th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: '#8b5cf6', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #dde1e7', whiteSpace: 'nowrap' }}>
                      {fbResult?.berater?.[1]?.name || 'Berater 2'}
                    </th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #dde1e7', whiteSpace: 'nowrap' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {fbResult?.days?.map((day) => {
                    if (day?.isWeekend) {
                      return (
                        <tr key={day?.date} style={{ background: '#f8fafc' }}>
                          <td style={{ padding: '8px 12px', color: '#94a3b8', fontWeight: 600, whiteSpace: 'nowrap' }}>{formatDate(day?.date)}</td>
                          <td colSpan={4} style={{ padding: '8px 12px', color: '#94a3b8', fontSize: '11px', fontStyle: 'italic' }}>Wochenende — geschlossen</td>
                        </tr>
                      );
                    }
                    if (day?.slots?.length === 0) {
                      return (
                        <tr key={day?.date} style={{ background: '#f8fafc' }}>
                          <td style={{ padding: '8px 12px', color: '#94a3b8', fontWeight: 600, whiteSpace: 'nowrap' }}>{formatDate(day?.date)}</td>
                          <td colSpan={4} style={{ padding: '8px 12px', color: '#94a3b8', fontSize: '11px', fontStyle: 'italic' }}>Keine Slots</td>
                        </tr>
                      );
                    }
                    return day?.slots?.map((slot, si) => {
                      const colors = slotColor(slot?.berater0?.free, slot?.berater1?.free);
                      return (
                        <tr key={day?.date + '-' + slot?.time} style={{ background: si % 2 === 0 ? '#fff' : '#fafbfc', borderBottom: '1px solid #f1f5f9' }}>
                          {si === 0 && (
                            <td rowSpan={day?.slots?.length} style={{ padding: '8px 12px', color: '#1e2b38', fontWeight: 700, whiteSpace: 'nowrap', verticalAlign: 'top', borderRight: '1px solid #f1f5f9' }}>
                              {formatDate(day?.date)}
                            </td>
                          )}
                          <td style={{ padding: '6px 12px', color: '#475569', fontWeight: 600, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '12px' }}>{slot?.time}</td>
                          <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                            <span title={slot?.berater0?.free ? 'Frei' : (slot?.berater0?.reason || 'Belegt')} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '6px', fontSize: '14px', background: slot?.berater0?.free ? '#dcfce7' : '#fef2f2', border: '1px solid ' + (slot?.berater0?.free ? '#86efac' : '#fca5a5'), cursor: 'help' }}>
                              {slot?.berater0?.allDay ? '🏖' : slot?.berater0?.free ? '✓' : '✗'}
                            </span>
                          </td>
                          <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                            <span title={slot?.berater1?.free ? 'Frei' : (slot?.berater1?.reason || 'Belegt')} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '6px', fontSize: '14px', background: slot?.berater1?.free ? '#dcfce7' : '#fef2f2', border: '1px solid ' + (slot?.berater1?.free ? '#86efac' : '#fca5a5'), cursor: 'help' }}>
                              {slot?.berater1?.allDay ? '🏖' : slot?.berater1?.free ? '✓' : '✗'}
                            </span>
                          </td>
                          <td style={{ padding: '6px 12px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, background: colors?.bg, color: colors?.label === 'Beide belegt' ? '#dc2626' : colors?.label === 'Beide frei' ? '#16a34a' : '#c2410c', border: '1px solid ' + colors?.border }}>
                              {slot?.conflict ? '⚠ Konflikt' : slot?.bothFree ? '✓ Beide frei' : '◐ Einer frei'}
                            </span>
                          </td>
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!fbResult && !fbLoading && !fbError && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '13px', background: '#fff', borderRadius: '10px', border: '1px dashed #dde1e7' }}>
            Datumsbereich wählen und "Verfügbarkeit abfragen" klicken
          </div>
        )}
      </div>
      {/* Query controls (existing /api/slots test) */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <div>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Datum</div>
          <input type="date" value={date} onChange={(e) => setDate(e?.target?.value)} style={inputStyle} />
        </div>
        <div>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Beratungsart</div>
          <select value={type} onChange={(e) => setType(e?.target?.value)} style={inputStyle}>
            <option value="phone">Telefonberatung (30 Min)</option>
            <option value="onsite">Vor-Ort-Beratung (60 Min)</option>
          </select>
        </div>
        <button
          onClick={runTest}
          disabled={loading}
          style={{ padding: '9px 20px', borderRadius: '10px', border: 'none', background: '#3E4954', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
        >
          {loading ? 'Laedt...' : 'Kalender abfragen'}
        </button>
        <button
          onClick={runApiSlotsTest}
          disabled={apiTestLoading}
          style={{ padding: '9px 20px', borderRadius: '10px', border: '1.5px solid #3E4954', background: '#fff', color: '#3E4954', fontSize: '13px', fontWeight: 700, cursor: apiTestLoading ? 'not-allowed' : 'pointer', opacity: apiTestLoading ? 0.7 : 1 }}
        >
          {apiTestLoading ? 'Teste...' : '/api/slots testen'}
        </button>
      </div>
      {/* /api/slots raw test result */}
      {(apiTestResult !== null || apiTestError !== null) && (
        <div style={{ background: '#f8fafc', border: '1px solid #dde1e7', borderRadius: '12px', padding: '18px 20px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e2b38' }}>/api/slots - Testergebnis</div>
            {tokenBadgeStyle && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 12px', borderRadius: '100px', fontSize: '12px', fontWeight: 600, background: tokenBadgeStyle?.bg, color: tokenBadgeStyle?.color, border: '1px solid ' + tokenBadgeStyle?.border }}>
                {tokenBadgeStyle?.label}
              </span>
            )}
            {apiTestLatency !== null && (
              <span style={{ fontSize: '12px', color: '#64748b', background: '#e2e8f0', padding: '3px 10px', borderRadius: '100px', fontWeight: 600 }}>
                {apiTestLatency} ms
              </span>
            )}
          </div>
          {apiTestError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '10px 14px', marginBottom: '10px', fontSize: '12px', color: '#dc2626' }}>
              <strong>Fehler:</strong> {apiTestError}
            </div>
          )}
          {apiTestResult && (
            <div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Raw Response</div>
              <pre style={{ background: '#1e2b38', color: '#e2e8f0', borderRadius: '8px', padding: '14px 16px', fontSize: '11px', overflowX: 'auto', margin: 0, maxHeight: '260px', overflowY: 'auto', lineHeight: 1.5 }}>
                {JSON.stringify(apiTestResult, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
      {/* Slot query error */}
      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '10px', padding: '14px 18px', marginBottom: '16px', fontSize: '13px', color: '#dc2626' }}>
          <strong>Fehler:</strong> {error}
        </div>
      )}
      {/* Slot query results */}
      {result && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
            <span style={badge(result?.isReal)}>
              {result?.isReal ? 'Live-Daten (Graph API)' : 'Mock-Daten (Fallback - Graph API nicht erreichbar)'}
            </span>
            <span style={{ fontSize: '13px', color: '#64748b' }}>
              {allSlots?.length} freie Slots gefunden
            </span>
          </div>
          {allSlots?.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px', color: '#8a95a3', fontSize: '13px', background: '#f8fafc', borderRadius: '10px' }}>
              Keine freien Slots fuer diesen Tag
            </div>
          ) : (
            <div>
              {result?.morning?.length > 0 && (
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Vormittag</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {result?.morning?.map((slot, i) => (
                      <span key={i} style={slotChip}>{slot?.time} - {slot?.berater}</span>
                    ))}
                  </div>
                </div>
              )}
              {result?.afternoon?.length > 0 && (
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Nachmittag</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {result?.afternoon?.map((slot, i) => (
                      <span key={i} style={slotChip}>{slot?.time} - {slot?.berater}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {!result && !loading && !error && apiTestResult === null && !apiTestLoading && (
        <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '13px', background: '#f8fafc', borderRadius: '10px', border: '1px dashed #dde1e7', marginBottom: '20px' }}>
          Datum waehlen und Kalender abfragen oder /api/slots testen klicken
        </div>
      )}
      {/* === /api/create-event TEST BUTTON === */}
      <div style={{ background: '#f8fafc', border: '1px solid #dde1e7', borderRadius: '14px', padding: '20px 24px', marginBottom: '24px' }}>
        <div style={{ fontSize: '14px', fontWeight: 700, color: '#1e2b38', marginBottom: '4px' }}>/api/create-event testen</div>
        <div style={{ fontSize: '12px', color: '#8a95a3', marginBottom: '16px' }}>Outlook-Kalendereintrag direkt über die API erstellen — Berater, Datum, Uhrzeit und Art wählen</div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Berater</div>
            <select value={ceBerater} onChange={(e) => setCeBerater(Number(e?.target?.value))} style={{ ...inputStyle, width: '100%' }}>
              {BERATER?.map((b, i) => (
                <option key={b?.id || i} value={i}>{b?.name}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Datum</div>
            <input type="date" value={ceDate} onChange={(e) => setCeDate(e?.target?.value)} style={{ ...inputStyle, width: '100%' }} />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Uhrzeit</div>
            <input type="time" value={ceTime} onChange={(e) => setCeTime(e?.target?.value)} style={{ ...inputStyle, width: '100%' }} />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Art</div>
            <select value={ceType} onChange={(e) => setCeType(e?.target?.value)} style={{ ...inputStyle, width: '100%' }}>
              <option value="phone">Telefonberatung (30 Min)</option>
              <option value="onsite">Vor-Ort-Beratung (60 Min)</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Vorname</div>
            <input type="text" value={ceVorname} onChange={(e) => setCeVorname(e?.target?.value)} style={{ ...inputStyle, width: '100%' }} />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Nachname</div>
            <input type="text" value={ceNachname} onChange={(e) => setCeNachname(e?.target?.value)} style={{ ...inputStyle, width: '100%' }} />
          </div>
        </div>

        <button
          onClick={runCreateEventTest}
          disabled={ceLoading}
          style={{ padding: '9px 22px', borderRadius: '10px', border: 'none', background: '#7c3aed', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: ceLoading ? 'not-allowed' : 'pointer', opacity: ceLoading ? 0.7 : 1, display: 'inline-flex', alignItems: 'center', gap: '6px' }}
        >
          {ceLoading ? 'Erstelle Event...' : '📅 Event erstellen (Test)'}
        </button>

        {/* create-event result */}
        {(ceResult !== null || ceError !== null) && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 12px', borderRadius: '100px', fontSize: '12px', fontWeight: 700, background: ceResult?.success ? '#dcfce7' : '#fef2f2', color: ceResult?.success ? '#16a34a' : '#dc2626', border: '1px solid ' + (ceResult?.success ? '#86efac' : '#fca5a5') }}>
                {ceResult?.success ? '✓ Event erfolgreich erstellt' : '✗ Event-Erstellung fehlgeschlagen'}
              </span>
              {ceLatency !== null && (
                <span style={{ fontSize: '12px', color: '#64748b', background: '#e2e8f0', padding: '3px 10px', borderRadius: '100px', fontWeight: 600 }}>
                  {ceLatency} ms
                </span>
              )}
            </div>
            {ceError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '10px 14px', marginBottom: '10px', fontSize: '12px', color: '#dc2626' }}>
                <strong>Fehler:</strong> {ceError}
              </div>
            )}
            {ceResult?.success && (
              <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '10px 14px', marginBottom: '10px', fontSize: '12px', color: '#15803d' }}>
                <div><strong>Event ID:</strong> {ceResult?.eventId}</div>
                {ceResult?.webLink && (
                  <div style={{ marginTop: '4px' }}>
                    <strong>Outlook Link:</strong>{' '}
                    <a href={ceResult?.webLink} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>Im Kalender öffnen</a>
                  </div>
                )}
              </div>
            )}
            {ceResult && (
              <div>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Raw Response</div>
                <pre style={{ background: '#1e2b38', color: '#e2e8f0', borderRadius: '8px', padding: '14px 16px', fontSize: '11px', overflowX: 'auto', margin: 0, maxHeight: '200px', overflowY: 'auto', lineHeight: 1.5 }}>
                  {JSON.stringify(ceResult, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
      {/* === GEBUCHTE TERMINE === */}
      <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '28px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#1e2b38', marginBottom: '2px' }}>Gebuchte Termine</div>
            <div style={{ fontSize: '12px', color: '#8a95a3' }}>Alle Buchungen aus der Datenbank mit Outlook-Sync-Status</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {/* Sync latency badge */}
            {bookingsLatency !== null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 12px', borderRadius: '100px', fontSize: '11px', fontWeight: 600, background: bookingsLatency < 500 ? '#f0fdf4' : bookingsLatency < 2000 ? '#fefce8' : '#fef2f2', color: bookingsLatency < 500 ? '#15803d' : bookingsLatency < 2000 ? '#a16207' : '#dc2626', border: '1px solid ' + (bookingsLatency < 500 ? '#bbf7d0' : bookingsLatency < 2000 ? '#fef08a' : '#fca5a5') }}>
                ⚡ Sync: {bookingsLatency}ms
              </span>
            )}
            {bookingsLastSync && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 12px', borderRadius: '100px', fontSize: '11px', fontWeight: 600, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>
                🔄 {formatDateTime(bookingsLastSync)}
              </span>
            )}
            <button
              onClick={loadBookings}
              disabled={bookingsLoading}
              style={{ padding: '7px 16px', borderRadius: '8px', border: '1.5px solid #dde1e7', background: '#fff', color: '#3E4954', fontSize: '12px', fontWeight: 600, cursor: bookingsLoading ? 'not-allowed' : 'pointer', opacity: bookingsLoading ? 0.6 : 1 }}
            >
              {bookingsLoading ? 'Laedt...' : 'Aktualisieren'}
            </button>
          </div>
        </div>

        {bookingsLoading ? (
          <div style={{ textAlign: 'center', padding: '32px', color: '#94a3b8', fontSize: '13px' }}>Lade Buchungen...</div>
        ) : bookings?.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px', color: '#94a3b8', fontSize: '13px', background: '#f8fafc', borderRadius: '10px', border: '1px dashed #dde1e7' }}>
            Noch keine Buchungen vorhanden
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  {['Ref', 'Name', 'Berater', 'Datum', 'Uhrzeit', 'Art', 'Outlook Sync']?.map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', borderBottom: '1px solid #dde1e7' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bookings?.map((b, idx) => {
                  const synced = syncedRefs?.has(b?.booking_ref);
                  return (
                    <tr key={b?.id || idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <td style={{ padding: '10px 14px', color: '#64748b', fontFamily: 'monospace', fontSize: '11px', whiteSpace: 'nowrap' }}>{b?.booking_ref || '-'}</td>
                      <td style={{ padding: '10px 14px', color: '#1e2b38', fontWeight: 600, whiteSpace: 'nowrap' }}>{b?.vorname} {b?.nachname}</td>
                      <td style={{ padding: '10px 14px', color: '#1e2b38', whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 600 }}>{b?.berater_name || '-'}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>{b?.berater_email || ''}</div>
                      </td>
                      <td style={{ padding: '10px 14px', color: '#1e2b38', whiteSpace: 'nowrap' }}>{formatDate(b?.date)}</td>
                      <td style={{ padding: '10px 14px', color: '#1e2b38', whiteSpace: 'nowrap' }}>{b?.time || '-'}</td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                        <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, background: b?.type === 'onsite' ? '#ede9fe' : '#dbeafe', color: b?.type === 'onsite' ? '#7c3aed' : '#1d4ed8' }}>
                          {b?.type === 'onsite' ? 'Vor-Ort' : 'Telefon'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                        {synced ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '100px', fontSize: '11px', fontWeight: 700, background: '#dcfce7', color: '#16a34a', border: '1px solid #86efac' }}>
                            In Outlook erstellt
                          </span>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '100px', fontSize: '11px', fontWeight: 700, background: '#f1f5f9', color: '#94a3b8', border: '1px solid #dde1e7' }}>
                            Nicht synchronisiert
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default GraphApiPanel;
